import { useTranslation } from "@refinedev/core";

import { Box, Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { VerificationCodeInput } from "@/components/auth";
import { CardButton } from "@/components/common";

import { QRCodeSVG } from "qrcode.react";

export interface MfaTotpSetupProps {
  totpSecret: string | null;
  email: string;
  verificationCode: string;
  onCodeChange: (value: string) => void;
  onVerify: () => void;
  isSubmitting: boolean;
  errorMessage: string;
  showSetPreferred?: boolean;
  setAsPreferred?: boolean;
  onSetPreferredChange?: (value: boolean) => void;
  submitButtonLabel?: string;
}

/**
 * Reusable TOTP (Authenticator App) MFA setup component.
 * Used in both setup-mfa.tsx and profile ChangeMFAPanel.
 */
export const MfaTotpSetup: React.FC<MfaTotpSetupProps> = ({
  totpSecret,
  email,
  verificationCode,
  onCodeChange,
  onVerify,
  isSubmitting,
  errorMessage,
  showSetPreferred = false,
  setAsPreferred = false,
  onSetPreferredChange,
  submitButtonLabel,
}) => {
  const { palette } = useTheme();
  const { translate } = useTranslation();

  const issuer = "Vitall";
  const totpUri = totpSecret
    ? `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${totpSecret}&issuer=${encodeURIComponent(issuer)}`
    : "";

  return (
    <Stack spacing={2} mt={1.25}>
      {/* Instructions */}
      <Typography variant="body2" color="text.secondary">
        {translate("pages.mfa.totpInstructions", "Scan this barcode or enter this code into your authenticator app")}
      </Typography>

      {/* QR Code Container */}
      {totpSecret && (
        <Box display="flex" justifyContent="center" p={2} bgcolor={palette.background.paper} borderRadius={1}>
          <QRCodeSVG value={totpUri} size={150} level="M" />
        </Box>
      )}

      {/* Set as Preferred (Profile only) */}
      {showSetPreferred && totpSecret && (
        <>
          <Typography variant="caption" color="primary">
            {translate("pages.profile.mfa.askSetPreferred", {
              method: "Authenticator App",
            })}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={setAsPreferred}
                onChange={e => onSetPreferredChange?.(e.target.checked)}
                size="small"
                disabled={isSubmitting}
              />
            }
            label={<Typography variant="body1">{translate("pages.mfa.setTotpPreferred")}</Typography>}
          />
        </>
      )}

      {/* Code Instructions */}
      <Typography variant="body2" color="text.hint">
        {translate("pages.mfa.enterCodeInstructions", "After receiving a six-digit code, enter the code below")}
      </Typography>

      {/* Code Input */}
      <VerificationCodeInput value={verificationCode} onChange={onCodeChange} disabled={isSubmitting} />

      {/* Error Message */}
      {errorMessage && (
        <Typography variant="body2" color="error.dark" align="center">
          {errorMessage}
        </Typography>
      )}

      {/* Verify Button */}
      <CardButton onClick={onVerify} disabled={verificationCode.length !== 6 || isSubmitting}>
        {isSubmitting
          ? translate(submitButtonLabel ? "pages.profile.mfa.enablingButton" : "pages.mfa.verifying")
          : (submitButtonLabel ?? translate("pages.mfa.verify"))}
      </CardButton>
    </Stack>
  );
};
